import { useMemo, useState, useCallback } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Select,
  Button,
  Skeleton,
  SimpleGrid,
  Drawer,
  Divider,
  ActionIcon,
  Tooltip,
  Pagination,
  Modal,
  Textarea,
  TextInput,
  NumberInput,
  Badge,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconFileText,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconEye,
  IconCheck,
  IconX,
  IconSend,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import {
  useJournalEntries,
  useJournalEntryDetail,
  useCreateDraftJE,
  useSubmitJE,
  useApproveJE,
  useRejectJE,
} from '@/hooks/usePortalApi';
import type { JournalEntryFull } from '@/types';
import dayjs from 'dayjs';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'auto_approved', label: 'Auto-Approved' },
  { value: 'rejected', label: 'Rejected' },
];

type SortField = 'entry_date' | 'total_debit' | 'approval_status';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 20;

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}

interface DraftLine {
  account_number: string;
  debit: number;
  credit: number;
  memo: string;
}

export default function JournalEntries() {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>('entry_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [drawerEntryId, setDrawerEntryId] = useState<number | null>(null);
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [approveOpened, { open: openApprove, close: closeApprove }] = useDisclosure(false);
  const [rejectOpened, { open: openReject, close: closeReject }] = useDisclosure(false);

  const [actionEntryId, setActionEntryId] = useState<number | null>(null);
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  // Create form
  const [draftDate, setDraftDate] = useState<Date | null>(null);
  const [draftDesc, setDraftDesc] = useState('');
  const [draftLines, setDraftLines] = useState<DraftLine[]>([
    { account_number: '', debit: 0, credit: 0, memo: '' },
    { account_number: '', debit: 0, credit: 0, memo: '' },
  ]);

  const { data, isLoading } = useJournalEntries({
    approval_status: statusFilter || undefined,
  });
  const { data: drawerEntry } = useJournalEntryDetail(drawerEntryId ?? 0);

  const createMutation = useCreateDraftJE();
  const submitMutation = useSubmitJE();
  const approveMutation = useApproveJE();
  const rejectMutation = useRejectJE();

  const allEntries = data?.items ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allEntries;
    return [...allEntries].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'entry_date') {
        cmp = dayjs(a.entry_date).unix() - dayjs(b.entry_date).unix();
      } else if (sortField === 'total_debit') {
        cmp = a.total_debit - b.total_debit;
      } else {
        cmp = (a.approval_status ?? '').localeCompare(b.approval_status ?? '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allEntries, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const entries = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalDebits = allEntries.reduce((s, e) => s + e.total_debit, 0);
  const pendingCount = allEntries.filter((e) => e.approval_status === 'pending_review').length;
  const postedCount = allEntries.filter((e) => e.is_posted).length;

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  };

  const handleViewDrawer = useCallback(
    (entry: JournalEntryFull) => { setDrawerEntryId(entry.id); openDrawer(); },
    [openDrawer],
  );

  const handleSubmit = useCallback(
    (id: number) => {
      submitMutation.mutate(id, {
        onSuccess: () => notifications.show({ title: 'Submitted', message: 'Entry submitted for review', color: 'blue' }),
        onError: () => notifications.show({ title: 'Error', message: 'Failed to submit entry', color: 'red' }),
      });
    },
    [submitMutation],
  );

  const handleOpenApprove = (id: number) => { setActionEntryId(id); setApproveNotes(''); openApprove(); };
  const handleOpenReject = (id: number) => { setActionEntryId(id); setRejectReason(''); openReject(); };

  const handleConfirmApprove = () => {
    if (!actionEntryId) return;
    approveMutation.mutate({ id: actionEntryId, notes: approveNotes || undefined }, {
      onSuccess: () => { notifications.show({ title: 'Approved', message: 'Journal entry approved', color: 'green' }); closeApprove(); closeDrawer(); },
      onError: () => notifications.show({ title: 'Error', message: 'Approval failed', color: 'red' }),
    });
  };

  const handleConfirmReject = () => {
    if (!actionEntryId) return;
    rejectMutation.mutate({ id: actionEntryId, reason: rejectReason }, {
      onSuccess: () => { notifications.show({ title: 'Rejected', message: 'Journal entry rejected', color: 'orange' }); closeReject(); closeDrawer(); },
      onError: () => notifications.show({ title: 'Error', message: 'Rejection failed', color: 'red' }),
    });
  };

  const totalDraftDebits = draftLines.reduce((s, l) => s + l.debit, 0);
  const totalDraftCredits = draftLines.reduce((s, l) => s + l.credit, 0);
  const isBalanced = totalDraftDebits > 0 && totalDraftDebits === totalDraftCredits;

  const handleCreateDraft = () => {
    createMutation.mutate(
      {
        entry_date: draftDate?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
        memo: draftDesc,
        lines: draftLines.filter((l) => l.account_number !== ''),
      },
      {
        onSuccess: () => {
          notifications.show({ title: 'Created', message: 'Draft journal entry created', color: 'green' });
          closeCreate();
          setDraftLines([
            { account_number: '', debit: 0, credit: 0, memo: '' },
            { account_number: '', debit: 0, credit: 0, memo: '' },
          ]);
          setDraftDesc('');
          setDraftDate(null);
        },
        onError: () => notifications.show({ title: 'Error', message: 'Failed to create draft', color: 'red' }),
      },
    );
  };

  const updateLine = (idx: number, field: keyof DraftLine, value: string | number) => {
    setDraftLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(field)}>
      <Group gap={4} wrap="nowrap">{children}<SortIcon field={field} current={sortField} dir={sortDir} /></Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader
        title="Journal Entries"
        description="General ledger journal entries with approval workflow"
        actions={<Button leftSection={<IconPlus size={16} />} onClick={openCreate}>New Draft</Button>}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard title="Total Debits" value={formatCurrency(totalDebits)} icon={<IconFileText size={24} />} />
        <StatCard title="Pending Review" value={String(pendingCount)} icon={<IconFileText size={24} />} />
        <StatCard title="Posted" value={String(postedCount)} icon={<IconCheck size={24} />} />
      </SimpleGrid>

      <Group justify="flex-end">
        <Select
          placeholder="Filter by status"
          data={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v ?? ''); setPage(1); }}
          w={180}
          size="sm"
          clearable
        />
      </Group>

      {isLoading ? (
        <Stack gap="xs">{[...Array(5)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}</Stack>
      ) : entries.length === 0 ? (
        <EmptyState icon={<IconFileText size={30} />} title="No journal entries" description={statusFilter ? 'Try a different filter' : 'No journal entries have been created yet'} />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Entry #</Table.Th>
                  <ThSort field="entry_date">Date</ThSort>
                  <Table.Th>Description</Table.Th>
                  <ThSort field="approval_status">Status</ThSort>
                  <ThSort field="total_debit"><Text ta="right" inherit>Amount</Text></ThSort>
                  <Table.Th w={120} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {entries.map((entry) => (
                  <Table.Tr key={entry.id}>
                    <Table.Td><Text size="sm" fw={500} style={{ fontFamily: 'monospace' }}>{entry.entry_number}</Text></Table.Td>
                    <Table.Td><Text size="sm">{dayjs(entry.entry_date).format('MMM D, YYYY')}</Text></Table.Td>
                    <Table.Td><Text size="sm" lineClamp={1}>{entry.memo}</Text></Table.Td>
                    <Table.Td><StatusBadge status={entry.approval_status} /></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={500}>{formatCurrency(entry.total_debit)}</Text></Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="View details">
                          <ActionIcon variant="subtle" size="sm" onClick={() => handleViewDrawer(entry)}>
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        {entry.approval_status === 'draft' && (
                          <Tooltip label="Submit for review">
                            <ActionIcon variant="subtle" size="sm" color="blue" onClick={() => handleSubmit(entry.id)} loading={submitMutation.isPending}>
                              <IconSend size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && <Group justify="center"><Pagination total={totalPages} value={page} onChange={setPage} /></Group>}
        </>
      )}

      {/* Detail Drawer */}
      <Drawer opened={drawerOpen} onClose={closeDrawer} position="right" size="lg" title={<Text fw={700} size="lg">{drawerEntry?.entry_number ?? 'Journal Entry'}</Text>}>
        {drawerEntry ? (
          <Stack gap="md">
            <Group justify="space-between">
              <StatusBadge status={drawerEntry.approval_status} />
              {drawerEntry.is_posted && <Badge color="green" variant="light">Posted</Badge>}
            </Group>
            <SimpleGrid cols={2}>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Date</Text><Text size="sm" fw={500}>{dayjs(drawerEntry.entry_date).format('MMM D, YYYY')}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Source</Text><Text size="sm" fw={500}>{drawerEntry.source}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Created By</Text><Text size="sm" fw={500}>{drawerEntry.created_by}</Text></div>
              {drawerEntry.approved_by && <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Approved By</Text><Text size="sm" fw={500}>{drawerEntry.approved_by}</Text></div>}
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total Debits</Text><Text size="sm" fw={600}>{formatCurrency(drawerEntry.total_debit)}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total Credits</Text><Text size="sm" fw={600}>{formatCurrency(drawerEntry.total_credit)}</Text></div>
            </SimpleGrid>
            <div><Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={4}>Memo</Text><Text size="sm">{drawerEntry.memo}</Text></div>
            {drawerEntry.rejection_reason && <div><Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={4}>Rejection Reason</Text><Text size="sm" c="red">{drawerEntry.rejection_reason}</Text></div>}
            {drawerEntry.lines && drawerEntry.lines.length > 0 && (
              <>
                <Divider />
                <Text fw={600} size="sm">Line Items</Text>
                <Table>
                  <Table.Thead><Table.Tr><Table.Th>Account</Table.Th><Table.Th ta="right">Debit</Table.Th><Table.Th ta="right">Credit</Table.Th><Table.Th>Memo</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {drawerEntry.lines.map((line) => (
                      <Table.Tr key={line.id}>
                        <Table.Td><Text size="sm">{line.account_number}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="sm">{line.debit > 0 ? formatCurrency(line.debit) : ''}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="sm">{line.credit > 0 ? formatCurrency(line.credit) : ''}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{line.memo || ''}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}
            {drawerEntry.approval_status === 'pending_review' && (
              <>
                <Divider />
                <Group>
                  <Button leftSection={<IconCheck size={16} />} onClick={() => handleOpenApprove(drawerEntry.id)}>Approve</Button>
                  <Button variant="light" color="red" leftSection={<IconX size={16} />} onClick={() => handleOpenReject(drawerEntry.id)}>Reject</Button>
                </Group>
              </>
            )}
            {drawerEntry.approval_status === 'draft' && (
              <>
                <Divider />
                <Button variant="light" color="blue" leftSection={<IconSend size={16} />} onClick={() => handleSubmit(drawerEntry.id)} loading={submitMutation.isPending}>Submit for Review</Button>
              </>
            )}
          </Stack>
        ) : (
          <Stack gap="xs">{[...Array(4)].map((_, i) => <Skeleton key={i} height={30} radius="md" />)}</Stack>
        )}
      </Drawer>

      {/* Create Draft Modal */}
      <Modal opened={createOpened} onClose={closeCreate} title={<Text fw={600}>New Journal Entry</Text>} size="lg">
        <Stack gap="md">
          <SimpleGrid cols={2}>
            <DateInput label="Entry Date" value={draftDate} onChange={setDraftDate} />
            <TextInput label="Description" value={draftDesc} onChange={(e) => setDraftDesc(e.currentTarget.value)} />
          </SimpleGrid>
          <Divider label="Lines" />
          {draftLines.map((line, idx) => (
            <Group key={idx} gap="xs" align="flex-end">
              <TextInput label={idx === 0 ? 'Account #' : undefined} value={line.account_number} onChange={(e) => updateLine(idx, 'account_number', e.currentTarget.value)} w={100} size="xs" />
              <NumberInput label={idx === 0 ? 'Debit' : undefined} prefix="$" value={line.debit || ''} onChange={(v) => updateLine(idx, 'debit', Number(v) || 0)} w={100} size="xs" min={0} />
              <NumberInput label={idx === 0 ? 'Credit' : undefined} prefix="$" value={line.credit || ''} onChange={(v) => updateLine(idx, 'credit', Number(v) || 0)} w={100} size="xs" min={0} />
              <TextInput label={idx === 0 ? 'Memo' : undefined} value={line.memo} onChange={(e) => updateLine(idx, 'memo', e.currentTarget.value)} style={{ flex: 1 }} size="xs" />
              <ActionIcon color="red" variant="subtle" onClick={() => setDraftLines((p) => p.filter((_, i) => i !== idx))} disabled={draftLines.length <= 2}><IconTrash size={14} /></ActionIcon>
            </Group>
          ))}
          <Button variant="subtle" size="xs" leftSection={<IconPlus size={14} />} onClick={() => setDraftLines((p) => [...p, { account_number: '', debit: 0, credit: 0, memo: '' }])}>Add Line</Button>
          <Group justify="space-between">
            <Badge color={isBalanced ? 'green' : 'red'} variant="light" size="lg">
              {isBalanced ? 'Balanced' : `Unbalanced (D: ${formatCurrency(totalDraftDebits)} / C: ${formatCurrency(totalDraftCredits)})`}
            </Badge>
            <Button onClick={handleCreateDraft} loading={createMutation.isPending} disabled={!isBalanced || !draftDesc}>Create Draft</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Approve Modal */}
      <Modal opened={approveOpened} onClose={closeApprove} title={<Text fw={600}>Approve Journal Entry</Text>} size="md">
        <Stack gap="md">
          <Textarea label="Notes (optional)" value={approveNotes} onChange={(e) => setApproveNotes(e.currentTarget.value)} minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeApprove}>Cancel</Button>
            <Button onClick={handleConfirmApprove} loading={approveMutation.isPending}>Approve</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reject Modal */}
      <Modal opened={rejectOpened} onClose={closeReject} title={<Text fw={600}>Reject Journal Entry</Text>} size="md">
        <Stack gap="md">
          <Textarea label="Reason" required value={rejectReason} onChange={(e) => setRejectReason(e.currentTarget.value)} minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeReject}>Cancel</Button>
            <Button color="red" onClick={handleConfirmReject} loading={rejectMutation.isPending} disabled={!rejectReason}>Reject</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
